/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const readValue = <T>(key: string, validator?: (value: T) => boolean) => {
  const readVal = JSON.parse(localStorage.getItem(key) ?? '') as T;
  if (validator !== undefined && !validator(readVal)) {
    throw new Error('validation failed');
  }
  return readVal;
};

const getInitialState = <T>(initialState: T | (() => T)) => {
  if (typeof initialState !== 'function') {
    return initialState;
  }
  return (initialState as () => T)();
};

export function useLocalStorage<T>(
  key: string,
  initialState: T | (() => T),
  validator?: (value: T) => boolean,
): [value: T, storeValue: (value: T) => void] {
  const currentKey = useRef(key);

  const [value, setValue] = useState<T>(() => {
    try {
      return readValue(key, validator);
    } catch (_e) {
      return getInitialState(initialState);
    }
  });

  const storeValue = useCallback(
    (newValue: T) => {
      const cleanedValue = newValue === undefined || newValue === '' ? null : newValue;
      localStorage.setItem(key, JSON.stringify(cleanedValue));
      setValue(newValue);
    },
    [key],
  );

  useEffect(() => {
    if (key !== currentKey.current) {
      setValue(() => {
        try {
          return readValue(key, validator);
        } catch (_e) {
          return getInitialState(initialState);
        }
      });
      currentKey.current = key;
    }
  }, [initialState, key, validator]);

  return [value, storeValue];
}
